import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CountdownTimer = () => {
  const [countdown, setCountdown] = useState({ enabled: false, endDate: '', title: '', subtitle: '' });
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const fetchCountdown = async () => {
      try {
        const res = await fetch(`${API_URL}/api/countdown`);
        if (res.ok) {
          const data = await res.json();
          setCountdown(data);
        }
      } catch (error) {
        console.error('Error fetching countdown:', error);
      }
    };
    fetchCountdown();
  }, []);

  useEffect(() => {
    if (!countdown.enabled || !countdown.endDate) return;

    const calculateTimeLeft = () => {
      const difference = new Date(countdown.endDate) - new Date();
      
      if (difference <= 0) {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  if (!countdown.enabled || isExpired) {
    return null;
  }

  const TimeBlock = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div 
        className="w-16 h-16 md:w-20 md:h-20 rounded-lg flex items-center justify-center text-2xl md:text-3xl font-bold shadow-lg"
        style={{ backgroundColor: '#5e3a1c', color: '#f5e9d0' }}
      >
        {String(value).padStart(2, '0')}
      </div>
      <span className="text-xs mt-1 uppercase tracking-wide" style={{ color: '#8b5c2f' }}>{label}</span>
    </div>
  );

  return (
    <section className="py-10" style={{ backgroundColor: '#f5e9d0' }}>
      <div className="container mx-auto px-6">
        <div 
          className="max-w-3xl mx-auto text-center p-8 rounded-2xl border-2 relative overflow-hidden"
          style={{ 
            borderColor: '#c2a357',
            background: 'linear-gradient(135deg, #fff9e6 0%, #f5e9d0 100%)'
          }}
        >
          {/* Decorative elements */}
          <div className="absolute top-4 left-4 text-4xl opacity-20">⏰</div>
          <div className="absolute bottom-4 right-4 text-4xl opacity-20">🍵</div>
          
          <div className="flex items-center justify-center gap-2 mb-4">
            <Clock className="w-6 h-6" style={{ color: '#c2a357' }} />
            <h3 className="text-xl md:text-2xl font-bold" style={{ color: '#5e3a1c' }}>
              {countdown.title || 'Limited Time Offer!'}
            </h3>
          </div>
          
          {countdown.subtitle && (
            <p className="mb-6" style={{ color: '#8b5c2f' }}>{countdown.subtitle}</p>
          )}
          
          <div className="flex justify-center gap-3 md:gap-6">
            <TimeBlock value={timeLeft.days} label="Days" />
            <div className="text-2xl font-bold self-center" style={{ color: '#c2a357' }}>:</div>
            <TimeBlock value={timeLeft.hours} label="Hours" />
            <div className="text-2xl font-bold self-center" style={{ color: '#c2a357' }}>:</div>
            <TimeBlock value={timeLeft.minutes} label="Mins" />
            <div className="text-2xl font-bold self-center" style={{ color: '#c2a357' }}>:</div>
            <TimeBlock value={timeLeft.seconds} label="Secs" />
          </div>
        </div>
      </div>
    </section>
  );
};
